import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import {
  getSurvicateInstance,
  initSurvicate,
  Survicate,
} from '@survicate/survicate-web-surveys-wrapper/widget_wrapper';

import { logger } from '../../utils/logger';

interface SurvicateContextType {
  invokeSurvey: (surveyEvent: string, traits?: Record<string, string | boolean>) => void;
}

const SurvicateContext = createContext<SurvicateContextType | undefined>(undefined);

export const SurvicateProvider = ({ children }: { children: ReactNode }) => {
  const [survicate, setSurvicate] = useState<Survicate | null>(null);
  useEffect(() => {
    const init = async () => {
      try {
        await initSurvicate({ workspaceKey: import.meta.env.VITE_SURVICATE_WORKSPACE });
      } catch (error) {
        logger.error('Failed to initialize Survicate', { error });
      }

      const survicateInstance = getSurvicateInstance();
      if (!survicateInstance) {
        logger.error('Survicate instance is not available after initialization');
        return;
      }
      setSurvicate(survicateInstance);
    };
    void init();
  }, []);

  const value: SurvicateContextType = {
    invokeSurvey: (surveyEvent, traits) => {
      if (traits) survicate?.setVisitorTraits(traits);
      survicate?.invokeEvent(surveyEvent);
    },
  };

  return <SurvicateContext.Provider value={value}>{children}</SurvicateContext.Provider>;
};

export const useSurvicate = (): SurvicateContextType => {
  const ctx = useContext(SurvicateContext);
  if (!ctx) throw new Error('useSurvicate must be used within SurvicateProvider');
  return ctx;
};
